import { ROUTING_CONFIG } from "../../../config/api";

/* ------------------------------------------------------------------ */
/*  Formatting helpers                                                 */
/* ------------------------------------------------------------------ */

export function formatDistance(meters) {
  if (meters == null || isNaN(meters)) return "";
  if (meters >= 10000) return `${Math.round(meters / 1000)} km`;
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
}

export function formatDuration(seconds) {
  if (seconds == null || isNaN(seconds)) return "";
  const min = Math.round(seconds / 60);
  if (min < 1) return "< 1 min";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

const COMPASS = ["north", "northeast", "east", "southeast", "south", "southwest", "west", "northwest"];
function bearingToCompass(bearing) {
  if (bearing == null || isNaN(bearing)) return "";
  return COMPASS[Math.round((((bearing % 360) + 360) % 360) / 45) % 8];
}

const MODIFIER_TEXT = {
  left: "left",
  right: "right",
  "sharp left": "sharp left",
  "sharp right": "sharp right",
  "slight left": "slightly left",
  "slight right": "slightly right",
  straight: "straight",
  uturn: "U-turn",
};

/**
 * OSRM koi ready-made text instruction nahi deta — sirf maneuver type/modifier.
 * Yahan usse readable narrative banate hain (English).
 *
 * @param {object} step      OSRM step
 * @param {object} ctx       { isFinalArrive: boolean, stopNumber: number }
 */
export function stepInstruction(step, ctx = {}) {
  const m = step.maneuver || {};
  const type = m.type;
  const mod = MODIFIER_TEXT[m.modifier] || m.modifier || "";
  const name = step.name ? ` onto ${step.name}` : "";
  const on = step.name ? ` on ${step.name}` : "";

  switch (type) {
    case "depart": {
      const dir = bearingToCompass(m.bearing_after);
      return `Head ${dir}${on}`.replace(/\s+/g, " ").trim();
    }
    case "arrive": {
      const side =
        m.modifier === "left" || m.modifier === "right"
          ? `, on the ${m.modifier}`
          : "";
      if (ctx.isFinalArrive) return `Arrive at your destination${side}`;
      return ctx.stopNumber
        ? `Arrive at stop ${ctx.stopNumber}${side}`
        : `Arrive at stop${side}`;
    }
    case "turn":
      if (m.modifier === "uturn") return `Make a U-turn${on}`;
      if (m.modifier === "straight") return `Continue straight${name}`;
      return `Turn ${mod}${name}`;
    case "new name":
    case "continue":
      if (m.modifier === "uturn") return `Make a U-turn${on}`;
      if (!m.modifier || m.modifier === "straight") {
        return step.name ? `Continue onto ${step.name}` : "Continue straight";
      }
      return `Continue ${mod}${name}`;
    case "merge":
      return `Merge ${mod}${name}`.replace(/\s+/g, " ");
    case "on ramp":
      return `Take the ramp ${mod}${name}`.replace(/\s+/g, " ");
    case "off ramp":
      return `Take the exit ${mod}${name}`.replace(/\s+/g, " ");
    case "fork":
      return `Keep ${mod} at the fork${name}`.replace(/\s+/g, " ");
    case "end of road":
      return `At the end of the road, turn ${mod}${name}`;
    case "roundabout":
    case "rotary":
      return m.exit
        ? `At the roundabout, take exit ${m.exit}${name}`
        : `Enter the roundabout${name}`;
    case "roundabout turn":
      return `At the roundabout, turn ${mod}${name}`;
    case "exit roundabout":
    case "exit rotary":
      return `Exit the roundabout${name}`;
    case "use lane":
      return `Use the lane ${mod}${name}`.replace(/\s+/g, " ");
    case "notification":
      return `Continue${on}`;
    default:
      return step.name ? `Continue onto ${step.name}` : "Continue";
  }
}

/* ------------------------------------------------------------------ */
/*  OSM Nominatim geocoding                                            */
/* ------------------------------------------------------------------ */

/**
 * @param {string} query
 * @param {number[]|null} viewbox  [minLon, minLat, maxLon, maxLat] — current
 *        map extent; results are BIASED towards it (bounded=0), not limited.
 * @returns {Promise<Array<{label:string, lon:number, lat:number}>>}
 */
export async function geocodeAddress(query, viewbox = null) {
  const q = (query || "").trim();
  if (!q) return [];
  const params = new URLSearchParams({
    q,
    format: "jsonv2",
    limit: "6",
    "accept-language": "en",
  });
  if (viewbox && viewbox.length === 4 && viewbox.every((v) => isFinite(v))) {
    const [minLon, minLat, maxLon, maxLat] = viewbox;
    params.set("viewbox", `${minLon},${maxLat},${maxLon},${minLat}`);
    params.set("bounded", "0");
  }
  const res = await fetch(`${ROUTING_CONFIG.nominatimUrl}/search?${params}`);
  if (!res.ok) throw new Error(`Geocoding failed (${res.status})`);
  const data = await res.json();
  return (Array.isArray(data) ? data : []).map((d) => ({
    label: d.display_name,
    lon: parseFloat(d.lon),
    lat: parseFloat(d.lat),
  }));
}

/** Map click ke liye — coordinate se readable naam. Fail ho to lat/lon string. */
export async function reverseGeocode(lon, lat) {
  const fallback = `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
  try {
    const params = new URLSearchParams({
      lat: String(lat),
      lon: String(lon),
      format: "jsonv2",
      "accept-language": "en",
    });
    const res = await fetch(`${ROUTING_CONFIG.nominatimUrl}/reverse?${params}`);
    if (!res.ok) return fallback;
    const data = await res.json();
    return data?.display_name || fallback;
  } catch (e) {
    return fallback;
  }
}

/* ------------------------------------------------------------------ */
/*  OSRM routing                                                       */
/* ------------------------------------------------------------------ */

/**
 * @param {Array<{lon:number, lat:number}>} stops  2+ placed stops, in order
 * @returns {Promise<{distance:number, duration:number, geometry:object,
 *                    legs:Array<{distance:number,duration:number,steps:Array}>}>}
 */
export async function fetchRoute(stops) {
  const placed = stops.filter((s) => s.lon != null && s.lat != null);
  if (placed.length < 2) throw new Error("At least 2 stops are required");

  const coords = placed.map((s) => `${s.lon},${s.lat}`).join(";");
  const params = new URLSearchParams({
    overview: "full",
    geometries: "geojson",
    steps: "true",
  });
  const url = `${ROUTING_CONFIG.osrmUrl}/route/v1/${ROUTING_CONFIG.osrmProfile}/${coords}?${params}`;

  const res = await fetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.code !== "Ok" || !data.routes?.length) {
    throw new Error(data.message || data.code || "No route found");
  }
  const r = data.routes[0];
  return {
    distance: r.distance,
    duration: r.duration,
    geometry: r.geometry, // GeoJSON LineString, lon/lat
    legs: r.legs.map((l) => ({
      distance: l.distance,
      duration: l.duration,
      steps: l.steps || [],
    })),
  };
}
